import { useState, useCallback, useEffect, useMemo } from 'react';
import type { Rule } from '@/types/entities';
import { PageHeader } from '@/components/composites/PageHeader';
import { FilterBar } from '@/components/composites/FilterBar';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { Button } from '@/components/primitives/Button';
import { InfoBanner } from '@/components/primitives/InfoBanner';
import { TextInput } from '@/components/primitives/TextInput';
import { useRules, useReclassify } from '@/hooks/useRules';
import { useProjects } from '@/hooks/useProjects';
import { useClients } from '@/hooks/useClients';
import { ApiError } from '@/lib/api';
import type { StatCardModel } from '@/types/stats';
import { AddRulePanel } from './rules/AddRulePanel';
import { RulePriorityRow } from './rules/RulePriorityRow';
import { ClientViewSection } from './rules/ClientViewSection';
import type { ClientGroupData } from './rules/ClientViewSection';
import styles from './ManageRulesPage.module.css';

type StatusFilter = 'all' | 'enabled' | 'disabled';
type ViewMode = 'priority' | 'client';

export function ManageRulesPage() {
  const { data: rules } = useRules();
  // Fetch archived projects too so the rules page can render the "paused —
  // target project archived" caption against rules whose target has been
  // archived. Rule rows include the archivedAt badge based on this list.
  const { data: projects } = useProjects({ includeArchived: true });
  const { data: clients } = useClients({ includeArchived: true });

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('priority');
  const [searchQuery, setSearchQuery] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  // Task 132 D5 — Reclassify button state. Confirm dialog + InfoBanner
  // feedback; the banner auto-clears after 6s. No toast primitive exists
  // in the app yet (see task 132 PR body note), so the fallback InfoBanner
  // path is the one taken here.
  const reclassify = useReclassify();
  const [reclassifyConfirmOpen, setReclassifyConfirmOpen] = useState(false);
  const [reclassifyFeedback, setReclassifyFeedback] = useState<{
    tone: 'success' | 'info' | 'danger';
    text: string;
  } | null>(null);

  useEffect(() => {
    if (reclassifyFeedback === null) return;
    const timer = window.setTimeout(() => setReclassifyFeedback(null), 6_000);
    return () => window.clearTimeout(timer);
  }, [reclassifyFeedback]);

  const reclassifyDisabled = rules.length === 0 || reclassify.isPending;
  const reclassifyTooltip =
    rules.length === 0 ? 'No rules to reclassify — add a rule first.' : undefined;

  const handleReclassifyConfirm = useCallback(async () => {
    setReclassifyFeedback(null);
    try {
      await reclassify.mutateAsync();
      setReclassifyConfirmOpen(false);
      setReclassifyFeedback({
        tone: 'success',
        text: 'Reclassification queued — the dashboard will update in about a minute.',
      });
    } catch (err) {
      setReclassifyConfirmOpen(false);
      // Discriminate on ApiError.code / .status rather than regex-matching
      // the message. The previous regex classifier silently fell through
      // to the generic "try again" toast on any non-409/429 failure (e.g.
      // a 404 from a stale dev container missing POST /reclassify, a 500,
      // a 403), masking the real problem. The danger fallback now surfaces
      // the HTTP status + structured code + message so operators have
      // something actionable to act on.
      if (err instanceof ApiError) {
        if (err.code === 'RECLASSIFY_IN_FLIGHT') {
          setReclassifyFeedback({
            tone: 'info',
            text: 'A reclassification is already running — wait for it to finish.',
          });
        } else if (err.code === 'RATE_LIMITED') {
          setReclassifyFeedback({
            tone: 'info',
            text: 'Reclassification was just triggered — wait 60 seconds before retrying.',
          });
        } else {
          setReclassifyFeedback({
            tone: 'danger',
            text: `Could not start reclassification (HTTP ${err.status}${err.code ? ` ${err.code}` : ''}): ${err.message}`,
          });
        }
      } else {
        // Network failure / non-HTTP error — preserve whatever message we got.
        const msg = err instanceof Error ? err.message : String(err);
        setReclassifyFeedback({
          tone: 'danger',
          text: `Could not start reclassification: ${msg}`,
        });
      }
    }
  }, [reclassify]);

  const enabledCount = rules.filter((r) => r.status === 'enabled').length;
  const disabledCount = rules.filter((r) => r.status === 'disabled').length;
  const projectsCovered = new Set(rules.map((r) => r.projectId)).size;

  const stats: [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Total Rules', value: String(rules.length) },
    { label: 'Enabled', value: String(enabledCount) },
    { label: 'Disabled', value: String(disabledCount) },
    { label: 'Projects Covered', value: String(projectsCovered) },
  ];

  const projectNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  // Task 121c: rules targeting an archived project render a "paused — target
  // project archived" caption and a muted Archived suffix next to the
  // project name. We need includeArchived projects in the lookup so the name
  // resolves even for archived targets.
  const archivedProjectIds = useMemo(() => {
    const s = new Set<string>();
    for (const p of projects) if (p.archivedAt) s.add(p.id);
    return s;
  }, [projects]);

  // Task 134 D1 — `editableProjects` is the list passed into the inline-edit
  // expansion's Project dropdown. Filter mirrors AddRulePanel's
  // `selectableProjects` rule but is stricter: `RulePriorityRow` itself
  // re-adds the rule's current project even if it's archived (defensive
  // fallback so an archived-target rule doesn't silently lose its target).
  const editableProjects = useMemo(
    () => projects.filter((p) => !p.archivedAt && !p.systemOwned),
    [projects],
  );

  const clientNameForProject = useMemo(() => {
    const clientMap = new Map<string, string>();
    for (const c of clients) clientMap.set(c.id, c.name);
    const m = new Map<string, string>();
    for (const p of projects) {
      if (p.clientId) {
        m.set(p.id, clientMap.get(p.clientId) ?? '\u2014');
      } else {
        m.set(p.id, '\u2014 (General)');
      }
    }
    return m;
  }, [projects, clients]);

  // Task 133 D2: case-insensitive substring match across rule.name, every
  // condition.value, every condition.type label, the target project name,
  // and the target client name. Both the Priority view and the Client view
  // depend on `filteredRules` so the search affects both consistently.
  const filteredRules = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return [...rules].filter((r) => {
      if (statusFilter === 'enabled' && r.status !== 'enabled') return false;
      if (statusFilter === 'disabled' && r.status !== 'disabled') return false;
      if (q === '') return true;
      if (r.name.toLowerCase().includes(q)) return true;
      const projectName = projectNameMap.get(r.projectId) ?? '';
      if (projectName.toLowerCase().includes(q)) return true;
      const clientName = clientNameForProject.get(r.projectId) ?? '';
      if (clientName.toLowerCase().includes(q)) return true;
      for (const c of r.conditions) {
        if (c.value.toLowerCase().includes(q)) return true;
        if (c.type.toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [rules, statusFilter, searchQuery, projectNameMap, clientNameForProject]);

  const handleRowClick = useCallback((id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  }, []);

  const closeExpansion = useCallback(() => {
    setExpandedRowId(null);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (panelOpen) setPanelOpen(false);
        else if (expandedRowId !== null) setExpandedRowId(null);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [panelOpen, expandedRowId]);

  const clientGroups = useMemo(() => {
    const map = new Map<string, Map<string, Rule[]>>();
    for (const r of filteredRules) {
      const cn = clientNameForProject.get(r.projectId) ?? '\u2014 (General)';
      const clientKey = cn === '\u2014 (General)' ? 'General' : cn;
      let projectMap = map.get(clientKey);
      if (!projectMap) {
        projectMap = new Map();
        map.set(clientKey, projectMap);
      }
      const projName = projectNameMap.get(r.projectId) ?? '\u2014';
      let ruleList = projectMap.get(projName);
      if (!ruleList) {
        ruleList = [];
        projectMap.set(projName, ruleList);
      }
      ruleList.push(r);
    }

    const groups: ClientGroupData[] = [];

    for (const [client, projectMap] of map) {
      const projectList = Array.from(projectMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, pRules]) => ({
          name,
          rules: pRules.sort((a, b) => a.priority - b.priority),
          enabledCount: pRules.filter((r) => r.status === 'enabled').length,
        }));
      groups.push({
        client,
        projects: projectList,
        totalRules: projectList.reduce((s, p) => s + p.rules.length, 0),
      });
    }

    groups.sort((a, b) => {
      if (a.client === 'General') return 1;
      if (b.client === 'General') return -1;
      return a.client.localeCompare(b.client);
    });

    return groups;
  }, [filteredRules, projectNameMap, clientNameForProject]);

  return (
    <>
      <PageHeader
        title="Rules"
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setReclassifyConfirmOpen(true)}
              disabled={reclassifyDisabled}
              title={reclassifyTooltip}
            >
              {reclassify.isPending ? 'Reclassifying…' : 'Reclassify'}
            </Button>
            <Button variant="primary" onClick={() => setPanelOpen(true)}>
              Add Rule
            </Button>
          </>
        }
      />

      {reclassifyFeedback && (
        <div className={styles.reclassifyFeedback}>
          <InfoBanner tone={reclassifyFeedback.tone}>{reclassifyFeedback.text}</InfoBanner>
        </div>
      )}

      <StatCardGrid cards={stats} />

      {viewMode === 'priority' && (
        <InfoBanner tone="info">
          Rules are evaluated in priority order (lowest number first). The first matching rule
          assigns the event to its project. Drag to reorder.
        </InfoBanner>
      )}
      {viewMode === 'client' && (
        <InfoBanner tone="info">
          Rules grouped by client and project. Click any group header to expand or collapse. Event
          counts reflect the current date range.
        </InfoBanner>
      )}

      {/* Filter Bar — Rule 36: filter rows render inside <FilterBar>, and
          segmented controls render as <Button variant="chip"> with
          aria-pressed driving the active state. */}
      <FilterBar
        tabs={
          <div className={styles.chipGroup} role="group" aria-label="Status filter">
            <Button
              variant="chip"
              size="sm"
              aria-pressed={statusFilter === 'all'}
              onClick={() => setStatusFilter('all')}
            >
              All
            </Button>
            <Button
              variant="chip"
              size="sm"
              aria-pressed={statusFilter === 'enabled'}
              onClick={() => setStatusFilter('enabled')}
            >
              Enabled
            </Button>
            <Button
              variant="chip"
              size="sm"
              aria-pressed={statusFilter === 'disabled'}
              onClick={() => setStatusFilter('disabled')}
            >
              Disabled
            </Button>
          </div>
        }
        search={
          <TextInput
            type="search"
            aria-label="Search rules"
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        }
        extraFilters={
          <>
            <div className={styles.chipGroup} role="group" aria-label="View mode">
              <Button
                variant="chip"
                size="sm"
                aria-pressed={viewMode === 'priority'}
                onClick={() => setViewMode('priority')}
              >
                Priority
              </Button>
              <Button
                variant="chip"
                size="sm"
                aria-pressed={viewMode === 'client'}
                onClick={() => setViewMode('client')}
              >
                Client
              </Button>
            </div>
            <span className={styles.filterCount}>
              Showing {filteredRules.length} of {rules.length} rules
            </span>
          </>
        }
      />

      {/* Priority View */}
      {viewMode === 'priority' && (
        <div className={styles.rulesTableCard}>
          <table className={styles.rulesTable}>
            <caption className={styles.srOnly}>Classification rules by priority</caption>
            <thead>
              <tr>
                <th className={styles.rulesTh} style={{ width: 28 }} />
                <th className={styles.rulesTh} style={{ width: 50, textAlign: 'center' }}>
                  #
                </th>
                <th className={styles.rulesTh}>Conditions</th>
                <th className={styles.rulesTh}>Project</th>
                <th className={styles.rulesTh}>Client</th>
                <th className={styles.rulesTh} style={{ width: 110 }}>
                  Name
                </th>
                <th className={`${styles.rulesTh} ${styles.rightAlign}`}>Events</th>
              </tr>
            </thead>
            <tbody>
              {filteredRules.map((r) => {
                const projectName = projectNameMap.get(r.projectId) ?? '\u2014';
                const clientName = clientNameForProject.get(r.projectId) ?? '\u2014';
                const isExpanded = expandedRowId === r.id;
                return (
                  <RulePriorityRow
                    key={r.id}
                    rule={r}
                    projectName={projectName}
                    projectArchived={archivedProjectIds.has(r.projectId)}
                    clientName={clientName}
                    editableProjects={editableProjects}
                    isExpanded={isExpanded}
                    onClick={() => handleRowClick(r.id)}
                    onCloseExpansion={closeExpansion}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Client View */}
      {viewMode === 'client' && <ClientViewSection groups={clientGroups} />}

      {/* Add Rule Panel */}
      {panelOpen && (
        <AddRulePanel
          projects={projects}
          clients={clients}
          nextPriority={rules.length + 1}
          onClose={() => setPanelOpen(false)}
        />
      )}

      {/* Reclassify confirm dialog (task 132 D5) */}
      {reclassifyConfirmOpen && (
        <div
          className={styles.confirmOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reclassify-confirm-title"
        >
          <div className={styles.confirmPanel}>
            <h3 id="reclassify-confirm-title" className={styles.confirmTitle}>
              Rebuild classifications for every historical event in this organisation?
            </h3>
            <p className={styles.confirmText}>
              This re-runs the current rule set against all events and may take up to a minute.
              Manual and AI-assisted classifications are preserved; only rule-matched and fallback
              (Not Set) assignments are recomputed.
            </p>
            <div className={styles.confirmActions}>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setReclassifyConfirmOpen(false)}
                disabled={reclassify.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                type="button"
                onClick={() => {
                  void handleReclassifyConfirm();
                }}
                disabled={reclassify.isPending}
              >
                {reclassify.isPending ? 'Reclassifying…' : 'Reclassify'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
